var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useRef, useState, memo, forwardRef, useCallback, useMemo, useEffect } from 'react';
import { X, CheckCircle, AlertCircle, ChevronDown, Search } from 'lucide-react';
import { Popover, PopoverTrigger, PopoverContent } from '../../ui/popover';
import { Checkbox } from '../../ui/checkbox';
import { Separator } from '../../ui/separator';
import { TooltipV2 } from '../../ui/tooltip-v2';
import { cn } from '../../../lib/utils.js';
import { useDropdownListScroll } from '../../../lib/dropdown-wheel-scroll.js';
import { useVirtualizer } from "@tanstack/react-virtual";
export var MultiSelectCheckboxTag = forwardRef(function (_a, ref) {
    var label = _a.label, _b = _a.placeholder, placeholder = _b === void 0 ? 'Placeholder' : _b, value = _a.value, onChange = _a.onChange, options = _a.options, _c = _a.state, state = _c === void 0 ? 'default' : _c, hint = _a.hint, error = _a.error, _d = _a.required, required = _d === void 0 ? false : _d, _e = _a.readOnly, readOnly = _e === void 0 ? false : _e, className = _a.className, containerClassName = _a.containerClassName, labelClassName = _a.labelClassName, fieldClassName = _a.fieldClassName, hintClassName = _a.hintClassName, id = _a.id, ariaLabel = _a["aria-label"], ariaDescribedBy = _a["aria-describedby"], dataTestId = _a["data-testid"], _f = _a.virtualizationThreshold, virtualizationThreshold = _f === void 0 ? 100 : _f, _g = _a.itemHeight, itemHeight = _g === void 0 ? 40 : _g, _h = _a.maxHeight, maxHeight = _h === void 0 ? 240 : _h, _j = _a.showSelectAll, showSelectAll = _j === void 0 ? true : _j, _k = _a.creatable, creatable = _k === void 0 ? false : _k, onCreateOption = _a.onCreateOption, tagMaxCharacters = _a.tagMaxCharacters;
    var _l = useState(false), open = _l[0], setOpen = _l[1];
    var _m = useState(''), search = _m[0], setSearch = _m[1];
    var _o = useState(false), tooltipOpen = _o[0], setTooltipOpen = _o[1];
    var inputRef = useRef(null);
    var listRef = useRef(null);
    var _p = useState(null), listNode = _p[0], setListNode = _p[1];
    var tagsContainerRef = useRef(null);
    var tagRefsRef = useRef(new Map());
    var _q = useState(null), visibleTagCount = _q[0], setVisibleTagCount = _q[1];
    var calculationRetryCountRef = useRef(0);
    var calculationTimeoutRef = useRef(null);
    var validValuesChangingRef = useRef(false);
    var measuredTagValuesRef = useRef(new Set());
    var MAX_RETRIES = 10; // Maximum number of retries before giving up
    var isDisabled = state === 'disabled';
    var isReadonly = state === 'readonly' || readOnly;
    var isError = state === 'error';
    var isSuccess = state === 'success';
    // Generate unique IDs for accessibility
    var inputId = id || "multi-select-checkbox-tag-".concat(Math.random().toString(36).substr(2, 9));
    var hintId = "".concat(inputId, "-hint");
    var errorId = "".concat(inputId, "-error");
    // Filtering logic - show all options (including selected ones) in the dropdown
    var filteredOptions = useMemo(function () {
        return options.filter(function (opt) {
            return opt.label.toLowerCase().includes(search.toLowerCase());
        });
    }, [options, search]);
    // Check if we should show "create option" - when search doesn't match and creatable is enabled
    var showCreateOption = useMemo(function () {
        if (!creatable || !search.trim())
            return false;
        var searchLower = search.trim().toLowerCase();
        // Show create option if search doesn't match any existing option
        return !options.some(function (opt) { return opt.label.toLowerCase() === searchLower; });
    }, [creatable, search, options]);
    // Filter value to only include values that exist in options
    var validValues = useMemo(function () {
        var optionsSet = new Set(options.map(function (opt) { return opt.value; }));
        return value.filter(function (v) { return optionsSet.has(v); });
    }, [value, options]);
    var prevValidValuesLengthRef = useRef(validValues.length);
    // Should we virtualize? (include create option in count if shown)
    var optionsCountForVirtualization = filteredOptions.length + (showCreateOption ? 1 : 0);
    var shouldVirtualize = optionsCountForVirtualization >= virtualizationThreshold;
    // Create virtualizer with callback ref support
    var virtualizer = useVirtualizer({
        count: filteredOptions.length,
        getScrollElement: useCallback(function () { return listRef.current; }, []),
        estimateSize: useCallback(function () { return itemHeight; }, [itemHeight]),
        overscan: 5,
    });
    // Ensure measurements are up-to-date when the dropdown opens
    useEffect(function () {
        if (open) {
            virtualizer.measure();
        }
    }, [open, virtualizer]);
    // Calculate visible tag count based on container width
    var calculateVisibleTags = useCallback(function () {
        if (!tagsContainerRef.current || validValues.length === 0) {
            setVisibleTagCount(function (prev) { return prev === null ? prev : null; }); // Only update if changed
            calculationRetryCountRef.current = 0; // Reset retry count
            return;
        }
        var container = tagsContainerRef.current;
        var containerWidth = container.clientWidth;
        if (containerWidth === 0) {
            // Container not yet measured, retry once
            if (calculationRetryCountRef.current < MAX_RETRIES) {
                calculationRetryCountRef.current++;
                requestAnimationFrame(function () {
                    setTimeout(function () { return calculateVisibleTags(); }, 0);
                });
            }
            return;
        }
        var gap = 8; // gap-2 = 8px
        var overflowIndicatorWidth = 50; // Approximate width for "+N" indicator
        // First, check if all tags are measured
        var allTagsMeasured = validValues.every(function (tagValue) {
            var tagElement = tagRefsRef.current.get(tagValue);
            return tagElement && tagElement.offsetWidth > 0;
        });
        // If not all tags are measured yet, wait a bit and retry (with limit)
        if (!allTagsMeasured) {
            if (calculationRetryCountRef.current < MAX_RETRIES) {
                calculationRetryCountRef.current++;
                requestAnimationFrame(function () {
                    setTimeout(function () { return calculateVisibleTags(); }, 0);
                });
                return; // Don't calculate yet, wait for retry
            }
            else {
                // Max retries reached - keep current state instead of making a wrong decision
                calculationRetryCountRef.current = 0;
                return; // Don't calculate with incomplete data
            }
        }
        else {
            // Reset retry count on success
            calculationRetryCountRef.current = 0;
        }
        // First pass: Check if all tags fit without the overflow indicator
        var usedWidth = 0;
        var allTagsFit = true;
        for (var i = 0; i < validValues.length; i++) {
            var tagValue = validValues[i];
            var tagElement = tagRefsRef.current.get(tagValue);
            if (tagElement && tagElement.offsetWidth > 0) {
                var tagWidth = tagElement.offsetWidth;
                var neededWidth = usedWidth + tagWidth + (i > 0 ? gap : 0);
                if (neededWidth <= containerWidth) {
                    usedWidth = neededWidth;
                }
                else {
                    allTagsFit = false;
                    break;
                }
            }
        }
        // If all tags fit, show all without indicator
        if (allTagsFit) {
            setVisibleTagCount(function (prev) { return prev === null ? prev : null; });
            return;
        }
        // Second pass: Calculate how many tags fit WITH the overflow indicator space reserved
        usedWidth = 0;
        var visibleCount = 0;
        for (var i = 0; i < validValues.length; i++) {
            var tagValue = validValues[i];
            var tagElement = tagRefsRef.current.get(tagValue);
            if (tagElement && tagElement.offsetWidth > 0) {
                var tagWidth = tagElement.offsetWidth;
                var neededWidth = usedWidth + tagWidth + (visibleCount > 0 ? gap : 0);
                // Always reserve space for overflow indicator (since we know not all tags fit)
                var totalNeededWidth = neededWidth + overflowIndicatorWidth + gap;
                if (totalNeededWidth <= containerWidth) {
                    usedWidth = neededWidth;
                    visibleCount++;
                }
                else {
                    break;
                }
            }
        }
        // Set the visible count only if it changed (prevents unnecessary re-renders)
        setVisibleTagCount(function (prev) { return prev === visibleCount ? prev : visibleCount; });
    }, [validValues]);
    // Create a stable reference based on validValues content (not array reference)
    var validValuesKey = validValues.join(',');
    // Reset retry count when validValues changes
    useEffect(function () {
        calculationRetryCountRef.current = 0;
        var prevLength = prevValidValuesLengthRef.current;
        var hasRemoved = validValues.length < prevLength;
        var shouldResetVisibleCount = visibleTagCount !== null && validValues.length > 0 && (hasRemoved || visibleTagCount >= validValues.length);
        if (shouldResetVisibleCount) {
            setVisibleTagCount(null);
        }
        // Set flag to prevent ref callbacks from triggering during validValues change
        validValuesChangingRef.current = true;
        // Clear any pending debounced calculations since useEffect will handle recalculation
        if (calculationTimeoutRef.current) {
            clearTimeout(calculationTimeoutRef.current);
            calculationTimeoutRef.current = null;
        }
        // Clear the flag after a longer delay to ensure all ref callbacks complete
        var timeoutId = setTimeout(function () {
            validValuesChangingRef.current = false;
        }, 200);
        prevValidValuesLengthRef.current = validValues.length;
        return function () { return clearTimeout(timeoutId); };
    }, [validValuesKey, visibleTagCount]); // Use stable key instead of array reference
    // Clean up tag refs and measured tags when tags are removed
    useEffect(function () {
        var currentTagValues = new Set(validValues);
        // Remove refs for tags that are no longer in validValues
        Array.from(tagRefsRef.current.keys()).forEach(function (tagValue) {
            if (!currentTagValues.has(tagValue)) {
                tagRefsRef.current.delete(tagValue);
                measuredTagValuesRef.current.delete(tagValue);
            }
        });
    }, [validValuesKey]); // Use stable key based on content
    // Debounced calculation function for batching tag ref measurements
    var debouncedCalculateVisibleTags = useCallback(function () {
        if (calculationTimeoutRef.current) {
            clearTimeout(calculationTimeoutRef.current);
        }
        calculationTimeoutRef.current = setTimeout(function () {
            requestAnimationFrame(function () {
                calculateVisibleTags();
            });
        }, 0); // Immediate but batched via setTimeout to collect all ref callbacks in the same tick
    }, [calculateVisibleTags]);
    // Recalculate when validValues changes or container resizes
    useEffect(function () {
        var timeoutId = null;
        var resizeObserver = null;
        // Use requestAnimationFrame to ensure DOM has updated
        var rafId = requestAnimationFrame(function () {
            timeoutId = setTimeout(function () {
                calculateVisibleTags();
            }, 0);
            // Recalculate on container resize
            resizeObserver = new ResizeObserver(function () {
                requestAnimationFrame(function () {
                    calculateVisibleTags();
                });
            });
            if (tagsContainerRef.current) {
                resizeObserver.observe(tagsContainerRef.current);
            }
        });
        return function () {
            cancelAnimationFrame(rafId);
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
            if (resizeObserver) {
                resizeObserver.disconnect();
            }
        };
    }, [validValuesKey]); // Use stable key based on content, not array reference
    // Callback ref to force virtualizer recalculation when ref is set
    var setListRef = useCallback(function (node) {
        listRef.current = node;
        setListNode(node);
        // Force virtualizer to recalculate when ref is set
        if (node && open) {
            virtualizer.measure();
        }
    }, [virtualizer, open]);
    useDropdownListScroll(listNode, open);
    // Check if all filtered options are selected
    var isAllSelected = useMemo(function () {
        return filteredOptions.length > 0 && filteredOptions.every(function (opt) { return validValues.includes(opt.value); });
    }, [filteredOptions, validValues]);
    // Base classes
    var containerClasses = cn('flex flex-col gap-1 w-[424px]', containerClassName, className);
    var labelClasses = cn('text-[#3D3D3D] dark:text-foreground font-medium text-[14px] leading-[18px] mb-1 flex items-center gap-0.5', labelClassName);
    var fieldClasses = cn('flex justify-between items-center w-full min-h-[40px] rounded-[8px] border px-2 py-1 transition-colors duration-150', {
        'border-[#D90B0B] dark:border-destructive': isError,
        'border-[#009735] dark:border-success': isSuccess,
        'border-[#CCC8C6] dark:border-input': !isError && !isSuccess && !open,
        'border-[#A09D9B] dark:border-ring': open,
        'bg-[#F7F4F2] dark:bg-muted': isDisabled || isReadonly,
        'bg-white dark:bg-background': !isDisabled && !isReadonly,
        'opacity-50 cursor-not-allowed': isDisabled,
        'cursor-default': isReadonly,
        'hover:border-[#A09D9B] dark:hover:border-ring': !isDisabled && !isReadonly,
    }, fieldClassName);
    var hintClasses = cn('text-xs mt-1', {
        'text-[#B21D1D] dark:text-destructive': isError,
        'text-[#757271] dark:text-muted-foreground': !isError,
    }, hintClassName);
    var requiredClasses = cn('text-destructive ml-0.5', {
        'hidden': !required,
    });
    var handleRemoveTag = function (tag) {
        if (isDisabled || isReadonly)
            return;
        onChange(value.filter(function (t) { return t !== tag; }));
    };
    var handleItemSelect = function (optionValue, checked) {
        if (isDisabled || isReadonly)
            return;
        if (checked) {
            if (!value.includes(optionValue)) {
                onChange(__spreadArray(__spreadArray([], value, true), [optionValue], false));
            }
        }
        else {
            onChange(value.filter(function (v) { return v !== optionValue; }));
        }
    };
    var handleCreateOption = function () {
        if (isDisabled || isReadonly || !creatable || !search.trim() || !onCreateOption)
            return;
        var newOption = onCreateOption(search.trim());
        if (newOption) {
            // Add the new option to the value array
            if (!value.includes(newOption.value)) {
                onChange(__spreadArray(__spreadArray([], value, true), [newOption.value], false));
            }
            // Clear the search
            setSearch('');
            // Note: The parent component should handle adding the new option to the options array
            // This component just triggers the creation via the callback
        }
    };
    var handleSelectAll = function (checked) {
        if (isDisabled || isReadonly)
            return;
        if (checked === true) {
            // Add all filtered options that aren't already selected
            var newValues = Array.from(new Set(__spreadArray(__spreadArray([], value, true), filteredOptions.map(function (opt) { return opt.value; }), true)));
            onChange(newValues);
        }
        else {
            // Remove all filtered options from selection
            var filteredValues_1 = filteredOptions.map(function (opt) { return opt.value; });
            onChange(value.filter(function (v) { return !filteredValues_1.includes(v); }));
        }
    };
    var handleInputKeyDown = function (e) {
        if (isDisabled || isReadonly)
            return;
        if (e.key === 'Backspace' && !search) {
            return;
        }
        // Handle Enter key to create new option
        if (e.key === 'Enter' && showCreateOption && creatable && onCreateOption) {
            e.preventDefault();
            handleCreateOption();
        }
    };
    return (_jsxs("div", { className: containerClasses, "data-testid": "".concat(dataTestId, "-container"), children: [label && (_jsxs("label", { htmlFor: inputId, className: labelClasses, "data-testid": "".concat(dataTestId, "-label"), children: [label, _jsx("span", { className: requiredClasses, "aria-hidden": "true", children: "*" })] })), _jsxs(Popover, { open: open, onOpenChange: setOpen, children: [_jsx(PopoverTrigger, { asChild: true, children: _jsxs("button", { ref: ref, type: "button", id: inputId, className: fieldClasses, disabled: isDisabled, tabIndex: 0, "aria-haspopup": "listbox", "aria-expanded": open, "aria-label": ariaLabel || label, "aria-invalid": isError, "aria-readonly": isReadonly, "aria-describedby": [
                                hint ? hintId : null,
                                isError ? errorId : null,
                                ariaDescribedBy
                            ].filter(Boolean).join(' ') || undefined, "data-testid": "".concat(dataTestId, "-field"), children: [_jsx("div", { ref: tagsContainerRef, className: "flex flex-row flex-nowrap items-center gap-2 w-full min-h-[36px] overflow-hidden", "data-testid": "".concat(dataTestId, "-tags-container"), children: validValues.length > 0 ? (_jsx(_Fragment, { children: validValues.map(function (tagValue, index) {
                                            var tag = options.find(function (opt) { return opt.value === tagValue; });
                                            var isVisible = visibleTagCount === null || index < visibleTagCount;
                                            if (!tag)
                                                return null; // Safety check (shouldn't happen with validValues)
                                            // Show overflow indicator if we've reached the visible limit
                                            if (visibleTagCount !== null && index === visibleTagCount) {
                                                var hiddenCount = validValues.length - visibleTagCount;
                                                var hiddenItems = validValues
                                                    .slice(visibleTagCount)
                                                    .map(function (v) { return options.find(function (opt) { return opt.value === v; }); })
                                                    .filter(function (tag) { return tag !== undefined; })
                                                    .map(function (tag) { return tag.label; });
                                                return (_jsx(TooltipV2, { open: tooltipOpen, onOpenChange: setTooltipOpen, disableHoverableContent: false, onPointerDownOutside: function () {
                                                        // Only close when clicking outside the tooltip
                                                        setTooltipOpen(false);
                                                    }, contentClassName: "z-[200] border border-border bg-popover text-popover-foreground shadow-md", content: _jsx("div", { className: "flex flex-col gap-2 max-h-48 overflow-y-auto text-popover-foreground", onMouseDown: function (e) {
                                                            // Prevent tooltip from closing when clicking inside
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                        }, children: validValues
                                                            .slice(visibleTagCount)
                                                            .map(function (v) { return options.find(function (opt) { return opt.value === v; }); })
                                                            .filter(function (tag) { return tag !== undefined; })
                                                            .map(function (tag) { return (_jsxs("div", { className: "flex items-center justify-between gap-3 text-xs group", children: [_jsx("span", { className: "truncate text-popover-foreground", children: tag.label }), !isDisabled && !isReadonly && (_jsx("button", { type: "button", className: "p-0.5 text-muted-foreground hover:text-foreground flex-shrink-0 transition-colors", onClick: function (e) {
                                                                        e.preventDefault();
                                                                        e.stopPropagation();
                                                                        handleRemoveTag(tag.value);
                                                                    }, onPointerDown: function (e) {
                                                                        e.preventDefault();
                                                                        e.stopPropagation();
                                                                    }, "aria-label": "Remove ".concat(tag.label), "data-testid": "".concat(dataTestId, "-tooltip-remove-").concat(tag.value), children: _jsx(X, { size: 12 }) }))] }, tag.value)); }) }), side: "top", align: "start", sideOffset: 8, children: _jsxs("span", { className: "flex items-center bg-[#E5E1DF] dark:bg-secondary rounded-full px-3 py-0.5 text-xs font-medium text-[#242424] dark:text-foreground opacity-80 h-[20px] min-w-max cursor-default", "data-testid": "".concat(dataTestId, "-overflow-indicator"), children: ["+", hiddenCount] }) }, "overflow-indicator"));
                                            }
                                            // Only render visible tags
                                            if (!isVisible)
                                                return null;
                                            var displayLabel = tagMaxCharacters != null && tag.label.length > tagMaxCharacters
                                                ? "".concat(tag.label.slice(0, tagMaxCharacters), "...")
                                                : tag.label;
                                            return (_jsxs("span", { ref: function (el) {
                                                    if (el) {
                                                        // Check if this tag VALUE has ever been measured (not just this DOM element)
                                                        var isNewTagValue = !measuredTagValuesRef.current.has(tag.value);
                                                        tagRefsRef.current.set(tag.value, el);
                                                        // Mark this tag value as measured
                                                        if (el.offsetWidth > 0) {
                                                            measuredTagValuesRef.current.add(tag.value);
                                                        }
                                                        // Only trigger recalculation for truly NEW tag values (not re-renders)
                                                        // Skip if validValues is changing (useEffect will handle that)
                                                        // Use debounced calculation to batch multiple tag measurements
                                                        if (isNewTagValue && !validValuesChangingRef.current && el.offsetWidth > 0) {
                                                            debouncedCalculateVisibleTags();
                                                        }
                                                    }
                                                    else {
                                                        tagRefsRef.current.delete(tag.value);
                                                        // Don't remove from measuredTagValuesRef - we want to remember we measured it
                                                    }
                                                }, className: "flex items-center bg-[#E5E1DF] dark:bg-secondary rounded-full px-3 py-0.5 text-xs font-medium text-[#242424] dark:text-foreground opacity-80 gap-1 h-[20px] min-w-max", style: tagMaxCharacters != null
                                                    ? { maxWidth: "calc(".concat(tagMaxCharacters + 3, "ch + 1.5rem + 1.25rem)") }
                                                    : undefined, title: tagMaxCharacters != null && tag.label.length > tagMaxCharacters ? tag.label : undefined, "data-testid": "".concat(dataTestId, "-tag-").concat(tag.value), children: [displayLabel, !isDisabled && !isReadonly && (_jsx("button", { type: "button", className: "ml-1 p-0.5 hover:bg-[#d6d3d1] dark:hover:bg-muted rounded-full", onClick: function (e) { e.stopPropagation(); handleRemoveTag(tag.value); }, tabIndex: -1, "aria-label": "Remove ".concat(tag.label), "data-testid": "".concat(dataTestId, "-remove-").concat(tag.value), children: _jsx(X, { size: 14, className: "text-[#585755] dark:text-muted-foreground" }) }))] }, tag.value));
                                        }) })) : (_jsx("span", { className: "text-[16px] font-normal leading-[130%] ".concat(isDisabled || isReadonly ? 'text-[#A09D9B] dark:text-muted-foreground' : 'text-[#757271] dark:text-muted-foreground'), children: placeholder })) }), _jsxs("div", { className: "flex items-center gap-1 h-full", children: [isError && _jsx(AlertCircle, { size: 18, className: "text-[#D90B0B] dark:text-destructive" }), isSuccess && _jsx(CheckCircle, { size: 18, className: "text-[#009735] dark:text-success" }), _jsx(ChevronDown, { size: 18, className: "text-[#757271] dark:text-muted-foreground" })] })] }) }), _jsxs(PopoverContent, { className: "w-[--radix-popover-trigger-width] p-0 rounded-[12px] border border-[#E2E8F0] dark:border-border bg-popover shadow flex flex-col gap-0 max-h-[314px] overflow-hidden", "data-testid": "".concat(dataTestId, "-popover"), children: [_jsx("div", { className: "flex items-center w-full min-w-0 box-border px-3 pt-3 pb-2 shrink-0", children: _jsxs("div", { "data-lift-select-search": true, className: "flex items-center w-full min-w-0 h-[34px] overflow-hidden bg-white dark:!bg-popover border border-[#E5E1DF] dark:border-[hsl(156_10%_36%)] rounded-[8px] px-2 gap-2", children: [_jsx(Search, { className: "h-[18px] w-[18px] text-[#757271] dark:text-muted-foreground shrink-0" }), _jsx("input", { ref: inputRef, type: "text", value: search, onChange: function (e) { return setSearch(e.target.value); }, placeholder: "Search", className: "flex-1 min-w-0 !bg-transparent dark:!bg-transparent outline-none !border-0 !ring-0 !shadow-none text-[14px] font-normal text-[#757271] dark:text-foreground placeholder:text-input-color dark:placeholder:text-muted-foreground font-[Figtree] appearance-none", style: {
                                                fontFamily: 'Figtree',
                                                backgroundColor: 'transparent',
                                                boxShadow: 'none',
                                                WebkitAppearance: 'none',
                                                border: 'none',
                                                outline: 'none',
                                                minWidth: 0,
                                            }, onKeyDown: handleInputKeyDown, autoFocus: true, disabled: isDisabled || isReadonly, readOnly: isReadonly, "data-testid": "".concat(dataTestId, "-search") })] }) }), _jsx(Separator, { className: "bg-gray-100 dark:bg-border shrink-0" }), _jsx("div", { ref: setListRef, className: "px-1 pb-2 min-h-0 overflow-y-auto overscroll-contain custom-scrollbar", role: "listbox", style: {
                                    height: shouldVirtualize ? maxHeight : Math.min(maxHeight, optionsCountForVirtualization * itemHeight),
                                    maxHeight: maxHeight,
                                    minHeight: optionsCountForVirtualization > 0 ? itemHeight : 60,
                                }, "data-testid": "".concat(dataTestId, "-options"), children: filteredOptions.length === 0 && search && !showCreateOption ? (_jsx("div", { className: "text-center text-[#757271] dark:text-muted-foreground py-4 text-[14px]", "data-testid": "".concat(dataTestId, "-no-options"), children: "No options" })) : (_jsxs(_Fragment, { children: [showCreateOption && (_jsxs("div", { className: "flex items-center space-x-2 px-2 py-1.5 hover:bg-[#F8FAFC] dark:hover:bg-accent group cursor-pointer", style: { height: "".concat(itemHeight, "px") }, onClick: handleCreateOption, "data-testid": "".concat(dataTestId, "-create-option"), children: [_jsx("div", { className: "h-4 w-4 flex-shrink-0 flex items-center justify-center", children: _jsx("span", { className: "text-[#009735] dark:text-success text-lg leading-none", children: "+" }) }), _jsxs("span", { className: "text-sm font-normal text-[#009735] dark:text-success cursor-pointer select-none group-hover:text-[#007A28] dark:group-hover:text-success truncate flex-1 min-w-0", children: ["Create \"", search.trim(), "\""] })] })), shouldVirtualize ? (
                                        // Virtualized rendering with safety checks
                                        _jsx("div", { style: {
                                                height: "".concat(virtualizer.getTotalSize(), "px"),
                                                width: '100%',
                                                position: 'relative',
                                                minHeight: itemHeight,
                                            }, children: virtualizer.getVirtualItems().length > 0 &&
                                                virtualizer.getVirtualItems().map(function (virtualItem) {
                                                    var opt = filteredOptions[virtualItem.index];
                                                    if (!opt)
                                                        return null;
                                                    var isSelected = value.includes(opt.value);
                                                    return (_jsxs("div", { className: "flex items-center space-x-2 px-2 py-1.5 hover:bg-[#F8FAFC] dark:hover:bg-accent group absolute left-0 top-0", style: {
                                                            transform: "translateY(".concat(virtualItem.start, "px)"),
                                                            height: "".concat(itemHeight, "px"),
                                                            width: '100%',
                                                        }, "data-testid": "".concat(dataTestId, "-option-").concat(opt.value), children: [_jsx(Checkbox, { id: "".concat(inputId, "-option-").concat(opt.value), checked: isSelected, onCheckedChange: function (checked) { return handleItemSelect(opt.value, checked); }, disabled: isDisabled || isReadonly, className: "h-4 w-4 flex-shrink-0", "data-testid": "".concat(dataTestId, "-checkbox-").concat(opt.value) }), _jsx("label", { htmlFor: "".concat(inputId, "-option-").concat(opt.value), className: "text-sm font-normal text-[#585755] dark:text-muted-foreground cursor-pointer select-none group-hover:text-[#242424] dark:group-hover:text-foreground truncate flex-1 min-w-0", title: opt.label, role: "option", "aria-selected": isSelected, children: opt.label })] }, opt.value));
                                                }) })) : (
                                        // Non-virtualized rendering
                                        filteredOptions.map(function (opt) {
                                            var isSelected = value.includes(opt.value);
                                            return (_jsxs("div", { className: "flex items-center space-x-2 px-2 py-1.5 hover:bg-[#F8FAFC] dark:hover:bg-accent group", style: { height: "".concat(itemHeight, "px") }, "data-testid": "".concat(dataTestId, "-option-").concat(opt.value), children: [_jsx(Checkbox, { id: "".concat(inputId, "-option-").concat(opt.value), checked: isSelected, onCheckedChange: function (checked) { return handleItemSelect(opt.value, checked); }, disabled: isDisabled || isReadonly, className: "h-4 w-4 flex-shrink-0", "data-testid": "".concat(dataTestId, "-checkbox-").concat(opt.value) }), _jsx("label", { htmlFor: "".concat(inputId, "-option-").concat(opt.value), className: "text-sm font-normal text-[#585755] dark:text-muted-foreground cursor-pointer select-none group-hover:text-[#242424] dark:group-hover:text-foreground truncate flex-1 min-w-0", title: opt.label, role: "option", "aria-selected": isSelected, children: opt.label })] }, opt.value));
                                        }))] })) }), showSelectAll && (_jsxs(_Fragment, { children: [_jsx(Separator, { className: "bg-gray-100 dark:bg-border shrink-0" }), _jsxs("div", { className: "flex items-center space-x-2 px-2 py-1.5 hover:bg-[#F8FAFC] dark:hover:bg-accent shrink-0", "data-testid": "".concat(dataTestId, "-select-all"), children: [_jsx(Checkbox, { id: "".concat(inputId, "-select-all"), checked: isAllSelected, onCheckedChange: handleSelectAll, disabled: isDisabled || isReadonly, className: "h-4 w-4", "data-testid": "".concat(dataTestId, "-checkbox-select-all") }), _jsx("label", { htmlFor: "".concat(inputId, "-select-all"), className: "text-sm font-normal text-[#242424] dark:text-foreground cursor-pointer select-none", children: "Select all" })] })] }))] })] }), (hint || isError) && (_jsx("div", { className: hintClasses, id: hint ? hintId : errorId, role: isError ? 'alert' : undefined, "data-testid": "".concat(dataTestId, "-hint"), children: isError ? error : hint }))] }));
});
MultiSelectCheckboxTag.displayName = 'MultiSelectCheckboxTag';
export default memo(MultiSelectCheckboxTag);
